import { CryptoPunks, ERC721, ERC20, Permit2, CreateVaultZap, InventoryStaking, PoolRouter, MarketplaceZap, NFTXVaultUpgradeable } from '@nftx/abi';
import { isCryptoPunk, getUniqueTokenIds, getContract, getChainConstant, addressEqual, getExactTokenIds, zipTokenIds } from '@nftx/utils';
import { Zero, MaxUint256, PERMIT2, USDC, WETH_TOKEN, WeiPerEther, NFTX_ROUTER, MARKETPLACE_ZAP } from '@nftx/constants';
import { ValidationError, QuoteFailedError, QuoteSlippageError, UnknownError } from '@nftx/errors';
import config from '@nftx/config';
import { zeroAddress, encodeAbiParameters, formatEther } from 'viem';

function approvePunk({
  tokenId,
  tokenIds,
  tokenAddress,
  provider,
  signer,
  spenderAddress
}) {
  if (!tokenId) {
    tokenId = tokenIds?.[0];
  }
  if (!tokenId) {
    throw new ValidationError({
      tokenId: 'To approve a PUNK you must provide the token ID'
    });
  }
  const contract = getContract({
    address: tokenAddress,
    provider,
    signer,
    abi: CryptoPunks
  });
  return contract.write.offerPunkForSaleToAddress({
    args: [BigInt(tokenId), Zero, spenderAddress]
  });
}
function approveErc721({
  tokenAddress,
  provider,
  signer,
  spenderAddress
}) {
  const contract = getContract({
    address: tokenAddress,
    provider,
    signer,
    abi: ERC721
  });
  return contract.write.setApprovalForAll({
    args: [spenderAddress, true]
  });
}
const approveErc1155 = approveErc721;
function approveErc20({
  tokenAddress,
  provider,
  signer,
  spenderAddress,
  amount
}) {
  const contract = getContract({
    address: tokenAddress,
    provider,
    signer,
    abi: ERC20
  });
  return contract.write.approve({
    args: [spenderAddress, amount ?? MaxUint256]
  });
}
async function approvePermit2({
  amount,
  network,
  provider,
  signer,
  spenderAddress,
  tokenAddress
}) {
  // 30 minutes
  const deadline = BigInt(Math.floor(Date.now() / 1000) + 60 * 30);
  const permit2Address = getChainConstant(PERMIT2, network);
  const contract = getContract({
    abi: Permit2,
    address: permit2Address,
    provider,
    signer
  });
  const [userAddress] = await signer.getAddresses();
  const [,, nonce] = await contract.read.allowance({
    args: [userAddress, tokenAddress, spenderAddress]
  });
  const PERMIT_DETAILS = [{
    name: 'token',
    type: 'address'
  }, {
    name: 'amount',
    type: 'uint160'
  }, {
    name: 'expiration',
    type: 'uint48'
  }, {
    name: 'nonce',
    type: 'uint48'
  }];
  const PERMIT_TYPES = {
    PermitSingle: [{
      name: 'details',
      type: 'PermitDetails'
    }, {
      name: 'spender',
      type: 'address'
    }, {
      name: 'sigDeadline',
      type: 'uint256'
    }],
    PermitDetails: PERMIT_DETAILS
  };
  const PERMIT_DOMAIN = {
    name: 'Permit2',
    chainId: network,
    verifyingContract: permit2Address
  };
  const permitSingleStruct = {
    details: {
      token: tokenAddress,
      amount,
      expiration: Number(deadline),
      nonce
    },
    spender: spenderAddress,
    sigDeadline: deadline
  };
  const promise = signer.signTypedData({
    account: userAddress,
    types: PERMIT_TYPES,
    domain: PERMIT_DOMAIN,
    primaryType: 'PermitSingle',
    message: permitSingleStruct
  });
  return {
    hash: zeroAddress,
    wait: async () => {
      const signature = await promise;
      const permit2encoded = encodeAbiParameters([{
        name: 'owner',
        type: 'address'
      }, {
        name: 'permitSingle',
        type: 'tuple',
        components: [{
          name: 'details',
          type: 'tuple',
          components: [{
            name: 'token',
            type: 'address'
          }, {
            name: 'amount',
            type: 'uint160'
          }, {
            name: 'expiration',
            type: 'uint48'
          }, {
            name: 'nonce',
            type: 'uint48'
          }]
        }, {
          name: 'spender',
          type: 'address'
        }, {
          name: 'sigDeadline',
          type: 'uint256'
        }]
      }, {
        name: 'signature',
        type: 'bytes'
      }], [userAddress, permitSingleStruct, signature]);
      return {
        blockHash: '0x',
        blockNumber: 0n,
        contractAddress: '0x',
        cumulativeGasUsed: 0n,
        effectiveGasPrice: 0n,
        from: userAddress,
        gasUsed: 0n,
        logs: [],
        logsBloom: '0x',
        status: 'success',
        to: permit2Address,
        transactionHash: '0x',
        transactionIndex: 0,
        type: 'legacy',
        permit2Signature: {
          signature,
          amount: permitSingleStruct.details.amount,
          expiration: permitSingleStruct.details.expiration,
          sigDeadline: permitSingleStruct.sigDeadline,
          nonce: permitSingleStruct.details.nonce,
          permit2encoded
        }
      };
    }
  };
}

/**
 * Approves a spender to spend a specific token address.
 * If you want to approve a standard sell or swap, use the relevant methods instead (approveSell/approveSwap)
 */
async function approve(args) {
  const {
    type = 'on-chain',
    network = config.network,
    tokenAddress,
    spenderAddress,
    tokenId,
    tokenIds,
    provider,
    signer,
    amount,
    standard = tokenId || tokenIds ? 'ERC721' : amount ? 'ERC20' : null
  } = args;
  if (type === 'permit2') {
    return approvePermit2({
      amount: amount ?? Zero,
      network,
      provider,
      signer,
      spenderAddress,
      tokenAddress
    });
  }
  if (standard === 'ERC721') {
    if (isCryptoPunk(tokenAddress)) {
      return approvePunk({
        tokenId,
        tokenIds: getUniqueTokenIds(tokenIds ?? []),
        tokenAddress,
        provider,
        signer,
        spenderAddress
      });
    }
    return approveErc721({
      tokenAddress,
      provider,
      signer,
      spenderAddress
    });
  }
  if (standard === 'ERC1155') {
    return approveErc1155({
      provider,
      signer,
      spenderAddress,
      tokenAddress
    });
  }
  if (standard === 'ERC20') {
    return approveErc20({
      tokenAddress,
      provider,
      signer,
      spenderAddress,
      amount
    });
  }
  throw new ValidationError({
    standard: `Approve is not supported for ${standard}`
  });
}

const isPunkApproved = async ({
  provider,
  tokenAddress,
  tokenId,
  userAddress,
  spenderAddress
}) => {
  try {
    const contract = getContract({
      provider,
      address: tokenAddress,
      abi: CryptoPunks
    });
    const [isForSale,, seller, minValue, onlySellTo] = await contract.read.punksOfferedForSale({
      args: [BigInt(tokenId)]
    });
    return [isForSale, addressEqual(seller, userAddress), minValue === Zero, addressEqual(onlySellTo, spenderAddress)].every(Boolean);
  } catch {
    return false;
  }
};
const arePunksApproved = async ({
  tokenIds,
  provider,
  spenderAddress,
  tokenAddress,
  userAddress
}) => {
  const results = await Promise.all(tokenIds.map(async tokenId => {
    return isPunkApproved({
      provider,
      spenderAddress,
      tokenAddress,
      tokenId,
      userAddress
    });
  }));
  return results.every(Boolean);
};
const isErc721Approved = async ({
  provider,
  tokenAddress,
  userAddress,
  spenderAddress
}) => {
  const contract = getContract({
    abi: ERC721,
    provider,
    address: tokenAddress
  });
  const approved = await contract.read.isApprovedForAll({
    args: [userAddress, spenderAddress]
  });
  return approved;
};
const isErc1155Approved = isErc721Approved;
const isErc20Approved = async ({
  provider,
  tokenAddress,
  userAddress,
  spenderAddress,
  amount
}) => {
  try {
    const contract = getContract({
      provider,
      abi: ERC20,
      address: tokenAddress
    });
    const allowance = await contract.read.allowance({
      args: [userAddress, spenderAddress]
    });
    return allowance > Zero && allowance >= (amount ?? MaxUint256);
  } catch {
    return false;
  }
};

/** Returns whether a spender is approved to spend a given token */
const isApproved = async args => {
  const {
    type,
    provider,
    spenderAddress,
    tokenAddress,
    userAddress,
    amount,
    tokenId
  } = args;
  let {
    tokenIds
  } = args;
  const {
    standard = tokenId || tokenIds ? 'ERC721' : 'ERC20'
  } = args;
  if (type === 'permit2') {
    return false;
  }
  if (standard === 'ERC721') {
    if (isCryptoPunk(tokenAddress)) {
      if (tokenIds == null || tokenIds.length === 0) {
        if (tokenId) {
          tokenIds = [tokenId];
        } else {
          return true;
        }
      }
      return arePunksApproved({
        provider,
        spenderAddress,
        tokenAddress,
        userAddress,
        tokenIds: getUniqueTokenIds(tokenIds)
      });
    }
    return isErc721Approved({
      provider,
      spenderAddress,
      tokenAddress,
      userAddress
    });
  }
  if (standard === 'ERC1155') {
    return isErc1155Approved({
      provider,
      tokenAddress,
      spenderAddress,
      userAddress
    });
  }
  if (standard === 'ERC20') {
    return isErc20Approved({
      amount,
      provider,
      spenderAddress,
      tokenAddress,
      userAddress
    });
  }
  return false;
};
var isApproved$1 = isApproved;

const getApproveContracts = ({
  label,
  network,
  spenderAddress,
  tokenAddress,
  amount,
  standard,
  tokenIds,
  usePermit2
}) => {
  if (isCryptoPunk(tokenAddress)) {
    return tokenIds?.map(tokenIdOrTuple => {
      const tokenIds = getUniqueTokenIds([tokenIdOrTuple]);
      return {
        type: 'on-chain',
        label: `${label} [${tokenIds}]`,
        tokenIds,
        spenderAddress,
        tokenAddress,
        standard
      };
    }) || [];
    // Permit2 is only available for ERC20 tokens
  } else if (usePermit2 && (standard === 'ERC20' || standard == null)) {
    return [{
      label: 'Approve Permit2',
      type: 'on-chain',
      spenderAddress: getChainConstant(PERMIT2, network),
      tokenAddress,
      standard: 'ERC20',
      amount: MaxUint256
    }, {
      type: 'permit2',
      label,
      spenderAddress,
      tokenAddress,
      amount,
      standard,
      tokenIds
    }];
  } else {
    return [{
      type: 'on-chain',
      label,
      spenderAddress,
      tokenAddress,
      amount,
      standard,
      tokenIds
    }];
  }
};

const makeCreateVault = ({
  getContract
}) => async function createVault({
  signer,
  provider,
  quote: {
    methodParameters: {
      params,
      value,
      contractAddress
    }
  }
}) {
  const contract = getContract({
    abi: CreateVaultZap,
    address: contractAddress,
    provider,
    signer
  });
  return contract.write.createVault({
    args: [params],
    value
  });
};
const createVault = makeCreateVault({
  getContract
});
var createVault$1 = createVault;

const createInventoryPosition = ({
  provider,
  quote: {
    methodParameters: {
      nftAmounts,
      nftIds,
      userAddress,
      vaultId,
      vToken,
      usePermit2,
      contractAddress
    }
  },
  signer,
  permit2
}) => {
  const contract = getContract({
    address: contractAddress,
    provider,
    abi: InventoryStaking,
    signer
  });

  // You can either dpeosit with vToken, or with NFTs
  if (vToken) {
    return contract.write.deposit({
      args: [BigInt(vaultId), vToken, userAddress, (usePermit2 ? permit2?.permit2encoded : undefined) || '0x', !!(usePermit2 && permit2), true]
    });
  }
  return contract.write.depositWithNFT({
    args: [BigInt(vaultId), nftIds, nftAmounts, userAddress]
  });
};
var createInventoryPosition$1 = createInventoryPosition;

const createLiquidityPosition = ({
  quote: {
    methodParameters: {
      params,
      value,
      usePermit2,
      contractAddress
    }
  },
  provider,
  signer,
  network = config.network,
  permit2
}) => {
  const contract = getContract({
    address: contractAddress,
    abi: PoolRouter,
    provider,
    signer
  });
  if (permit2 && usePermit2) {
    return contract.write.addLiquidityWithPermit2({
      args: [params, permit2.permit2encoded],
      value
    });
  }
  return contract.write.addLiquidity({
    args: [params],
    value
  });
};
var createLiquidityPosition$1 = createLiquidityPosition;

const increaseLiquidity = ({
  provider,
  quote: {
    methodParameters: {
      params,
      value,
      usePermit2,
      contractAddress
    }
  },
  signer,
  permit2
}) => {
  const contract = getContract({
    address: contractAddress,
    abi: PoolRouter,
    provider,
    signer
  });
  if (permit2 && usePermit2) {
    return contract.write.increaseLiquidityWithPermit2({
      args: [params, permit2.permit2encoded],
      value
    });
  }
  return contract.write.increaseLiquidity({
    args: [params],
    value
  });
};
var increaseLiquidity$1 = increaseLiquidity;

const withdrawInventory = ({
  provider,
  quote: {
    methodParameters: {
      nftIds,
      positionId,
      vTokenPremiumLimit,
      vTokenShares,
      value,
      contractAddress
    }
  },
  signer
}) => {
  const contract = getContract({
    abi: InventoryStaking,
    address: contractAddress,
    provider,
    signer
  });
  return contract.write.withdraw({
    args: [positionId, vTokenShares, nftIds, vTokenPremiumLimit],
    value
  });
};
var withdrawInventory$1 = withdrawInventory;

const withdrawLiquidity = ({
  provider,
  quote: {
    methodParameters: {
      params,
      value,
      contractAddress
    }
  },
  signer
}) => {
  const contract = getContract({
    abi: PoolRouter,
    address: contractAddress,
    provider,
    signer
  });
  return contract.write.removeLiquidity({
    args: [params],
    value
  });
};
var withdrawLiquidity$1 = withdrawLiquidity;

const parseQuoteToken = (address, network) => {
  switch (address) {
    case 'ETH':
      return 'ETH';
    case 'WETH':
      return getChainConstant(WETH_TOKEN, network);
    case 'USDC':
      return getChainConstant(USDC, network);
    default:
      return address;
  }
};
var parseQuoteToken$1 = parseQuoteToken;

const ammQuoteToPrice = quote => {
  const {
    network,
    sellToken
  } = quote;
  const isEth = `${sellToken}` === 'ETH';
  const totalAmountIn = quote.route.reduce((total, r) => {
    return total + BigInt(r[0].amountIn);
  }, Zero);
  const route = quote.route.map(route => {
    const type = route[0].type;
    const amountIn = BigInt(route[0].amountIn);
    const amountOut = BigInt(route[route.length - 1].amountOut);
    const proportion = formatEther(amountIn * WeiPerEther / totalAmountIn);
    return {
      type,
      amountIn,
      amountOut,
      proportion,
      tokenIn: {
        id: route[0].tokenIn.address,
        name: '',
        symbol: route[0].tokenIn.symbol
      },
      tokenOut: {
        id: route[route.length - 1].tokenOut.address,
        name: '',
        symbol: route[route.length - 1].tokenOut.symbol
      },
      path: route.map(r => ({
        tokenIn: {
          id: r.tokenIn.address,
          name: '',
          symbol: r.tokenIn.symbol
        },
        tokenOut: {
          id: r.tokenOut.address,
          name: '',
          symbol: r.tokenOut.symbol
        },
        amount: BigInt(r.amountIn || r.amountOut || '0')
      }))
    };
  });
  const approveContracts = [];

  // If you're paying ETH we don't need any approvals
  if (!isEth) {
    approveContracts.push(...getApproveContracts({
      network,
      label: `Approve ${route[0].tokenIn.symbol}`,
      spenderAddress: getChainConstant(NFTX_ROUTER, network),
      tokenAddress: sellToken,
      amount: BigInt(quote.amount),
      standard: 'ERC20',
      usePermit2: true
    }));
  }
  let methodParameters = undefined;
  if (quote.methodParameters) {
    methodParameters = {
      amountsIn: [],
      amountsOut: [],
      premiumLimit: '',
      standard: 'ERC20',
      tokenIdsIn: [],
      tokenIdsOut: [],
      vaultAddress: '0x',
      vaultId: '',
      executeCalldata: quote.methodParameters.calldata,
      to: quote.methodParameters.to,
      value: quote.methodParameters.value
    };
  }
  const price = {
    approveContracts,
    estimatedGas: BigInt(quote.gasUseEstimate),
    feePrice: Zero,
    items: [],
    premiumPrice: Zero,
    price: BigInt(quote.quote),
    route,
    type: 'erc20',
    vTokenPrice: BigInt(quote.quote),
    routeString: quote.routeString,
    methodParameters: methodParameters
  };
  return price;
};
var ammQuoteToPrice$1 = ammQuoteToPrice;

const fallbackQuote = {
  approveContracts: [],
  estimatedGas: Zero,
  feePrice: Zero,
  items: [],
  methodParameters: {},
  premiumPrice: Zero,
  price: Zero,
  vTokenPrice: Zero,
  type: 'erc20'
};
const fetchAmmQuote = async args => {
  const {
    network = config.network,
    buyAmount,
    sellAmount,
    slippagePercentage = 0.01,
    userAddress,
    permit2,
    throwOnError = true
  } = args;
  try {
    const sellToken = parseQuoteToken$1(args.sellToken, network);
    const buyToken = parseQuoteToken$1(args.buyToken, network);
    const baseUrl = getChainConstant(config.urls.NFTX_ROUTER_URL, network, null);
    ValidationError.validate({
      sellToken: () => !!sellToken || 'Required',
      network: () => {
        if (!network) {
          return 'Required';
        }
        if (!baseUrl) {
          return 'Not currently supported for NFTX Router';
        }
      },
      buyToken: () => !!buyToken || 'Required'
    });
    const searchParams = new URLSearchParams();
    searchParams.append('tokenInAddress', sellToken);
    searchParams.append('tokenInChainId', `${network}`);
    searchParams.append('tokenOutAddress', buyToken);
    searchParams.append('tokenOutChainId', `${network}`);
    if (buyAmount) {
      searchParams.append('amount', BigInt(buyAmount).toString());
      searchParams.append('type', 'exactOut');
    } else if (sellAmount) {
      searchParams.append('amount', BigInt(sellAmount).toString());
      searchParams.append('type', 'exactIn');
    } else {
      // Default to just buying 1
      searchParams.append('buyAmount', WeiPerEther.toString());
      searchParams.append('type', 'exactOut');
    }
    if (userAddress) {
      searchParams.append('recipient', userAddress);
      searchParams.append('deadline', '300');
      searchParams.append('enableUniversalRouter', 'true');
      searchParams.append('slippageTolerance', `${(slippagePercentage ?? 0.01) * 100}`);
      searchParams.append('intent', 'swap');
    } else {
      searchParams.append('intent', 'quote');
    }
    searchParams.append('protocols', 'v3');
    if (permit2) {
      searchParams.append('permitSignature', permit2.signature);
      searchParams.append('permitAmount', permit2.amount.toString());
      searchParams.append('permitExpiration', permit2.expiration.toString());
      searchParams.append('permitSigDeadline', permit2.sigDeadline.toString());
      searchParams.append('permitNonce', permit2.nonce.toString());
    }
    const query = searchParams.toString();
    const url = `${baseUrl}?${query}`;
    const response = await fetch(url);
    if (!response.ok) {
      const json = await response.json();
      throw new QuoteFailedError(json);
    }
    const data = await response.json();
    if (userAddress && !data?.methodParameters) {
      throw new QuoteSlippageError();
    }
    if (userAddress && config.debug) {
      console.debug(data);
    }
    if (data?.methodParameters?.to) {
      // We need to override the to property as it's pointing to the Uniswap router instead of our own
      data.methodParameters.to = getChainConstant(NFTX_ROUTER, network);
    }
    return ammQuoteToPrice$1({
      ...data,
      network,
      sellToken,
      buyToken
    });
  } catch (e) {
    if (!throwOnError) {
      return fallbackQuote;
    }
    throw e;
  }
};
var fetchAmmQuote$1 = fetchAmmQuote;

const buy = ({
  network = config.network,
  provider,
  quote: {
    methodParameters: params
  },
  signer
}) => {
  const contract = getContract({
    address: getChainConstant(MARKETPLACE_ZAP, network),
    abi: MarketplaceZap,
    provider,
    signer
  });
  const vaultId = BigInt(params.vaultId);
  const idsOut = getExactTokenIds(zipTokenIds(params.tokenIdsOut, params.amountsOut)).map(BigInt);
  const calldata = params.executeCalldata;
  const to = params.to;
  const vTokenPremiumLimit = BigInt(params.premiumLimit);
  const deductRoyalty = false;
  return contract.write.buyNFTsWithETH({
    args: [vaultId, idsOut, calldata, to, vTokenPremiumLimit, deductRoyalty],
    value: BigInt(params.value)
  });
};
var buy$1 = buy;

const mint = async ({
  provider,
  signer,
  quote: {
    methodParameters: params
  }
}) => {
  const contract = getContract({
    provider,
    signer,
    abi: NFTXVaultUpgradeable,
    address: params.vaultAddress
  });
  const tokenIds = params.tokenIdsIn.map(BigInt);
  const amounts = params.amountsIn.map(BigInt);
  const depositor = params.to;
  const to = params.to;
  const value = BigInt(params.value);

  // Not sure if vaultAddress is correct
  return contract.write.mint({
    args: [tokenIds, amounts, depositor, to],
    value
  });
};
var mint$1 = mint;

const redeem = async ({
  provider,
  signer,
  quote: {
    methodParameters: params
  }
}) => {
  const contract = getContract({
    provider,
    signer,
    abi: NFTXVaultUpgradeable,
    address: params.vaultAddress
  });
  const tokenIds = getExactTokenIds(zipTokenIds(params.tokenIdsOut, params.amountsOut)).map(BigInt);
  const to = params.to;
  const wethAmount = Zero;
  const forceFees = true;
  const value = BigInt(params.value);
  const vTokenPremiumLimit = BigInt(params.premiumLimit);
  return contract.write.redeem({
    args: [tokenIds, to, wethAmount, vTokenPremiumLimit, forceFees],
    value
  });
};
var redeem$1 = redeem;

const sell = ({
  network = config.network,
  provider,
  quote: {
    methodParameters: params
  },
  signer
}) => {
  const contract = getContract({
    address: getChainConstant(MARKETPLACE_ZAP, network),
    abi: MarketplaceZap,
    provider,
    signer
  });
  const vaultId = BigInt(params.vaultId);
  const idsIn = params.tokenIdsIn.map(BigInt);
  const amounts = params.amountsIn.map(BigInt);
  const calldata = params.executeCalldata;
  const to = params.to;
  // TODO: handle royalties?
  const deductRoyalty = false;
  if (params.standard === 'ERC1155') {
    return contract.write.sell1155({
      args: [vaultId, idsIn, amounts, calldata, to, deductRoyalty]
      // value,
    });
  }
  return contract.write.sell721({
    args: [vaultId, idsIn, calldata, to, deductRoyalty]
    // value,
  });
};
var sell$1 = sell;

const swap = ({
  network = config.network,
  provider,
  quote: {
    methodParameters: params
  },
  signer
}) => {
  const contract = getContract({
    address: getChainConstant(MARKETPLACE_ZAP, network),
    abi: MarketplaceZap,
    provider,
    signer
  });
  const vaultId = BigInt(params.vaultId);
  const idsIn = params.tokenIdsIn.map(BigInt);
  const amounts = params.amountsIn.map(BigInt);
  const idsOut = getExactTokenIds(zipTokenIds(params.tokenIdsOut, params.amountsOut)).map(BigInt);
  const vTokenPremiumLimit = BigInt(params.premiumLimit);
  const to = params.to;
  const value = BigInt(params.value);
  if (params.standard === 'ERC1155') {
    return contract.write.swap1155({
      args: [vaultId, idsIn, amounts, idsOut, vTokenPremiumLimit, to],
      value
    });
  }
  return contract.write.swap721({
    args: [vaultId, idsIn, idsOut, vTokenPremiumLimit, to],
    value
  });
};
var swap$1 = swap;

const tradeErc20 = async ({
  provider,
  signer,
  quote
}) => {
  const [address] = await signer.getAddresses();
  const account = address;
  const {
    methodParameters: {
      executeCalldata,
      to,
      value
    }
  } = quote;
  if (config.debug) {
    console.debug({
      method: 'tradeErc20',
      to,
      data: executeCalldata,
      value: BigInt(value),
      account
    });
  }
  const hash = await signer.sendTransaction({
    to,
    data: executeCalldata,
    value: BigInt(value),
    account,
    chain: provider.chain
  });
  return {
    hash,
    wait: () => provider.waitForTransactionReceipt({
      hash
    })
  };
};
var tradeErc20$1 = tradeErc20;

/** Fulfills any quote returned by @nftx/api's fetchQuote method */
const fulfill = ({
  network,
  quote,
  signer,
  provider
}) => {
  switch (quote.type) {
    case 'buy':
      return buy$1({
        provider,
        quote,
        signer,
        network
      });
    case 'sell':
      return sell$1({
        provider,
        quote,
        signer,
        network
      });
    case 'mint':
      return mint$1({
        provider,
        quote,
        signer
      });
    case 'redeem':
      return redeem$1({
        quote,
        provider,
        signer
      });
    case 'swap':
      return swap$1({
        quote,
        provider,
        signer,
        network
      });
    case 'erc20':
      return tradeErc20$1({
        provider,
        quote,
        signer,
        network
      });
    default:
      throw new UnknownError(`Unknown quote type: ${quote.type}`);
  }
};
var fulfill$1 = fulfill;

export { approve, buy$1 as buy, createInventoryPosition$1 as createInventoryPosition, createLiquidityPosition$1 as createLiquidityPosition, createVault$1 as createVault, fetchAmmQuote$1 as fetchAmmQuote, fulfill$1 as fulfill, getApproveContracts, increaseLiquidity$1 as increaseLiquidity, isApproved$1 as isApproved, mint$1 as mint, parseQuoteToken$1 as parseQuoteToken, redeem$1 as redeem, sell$1 as sell, swap$1 as swap, tradeErc20$1 as tradeErc20, withdrawInventory$1 as withdrawInventory, withdrawLiquidity$1 as withdrawLiquidity };
